import { useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { KpiStrip, MetricCard, Panel, SectionHeader } from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { fmtNumber } from '@/shared/lib/format'
import { RankedBars, OutcomeStrip, ChartFrame } from '@/shared/ui/charts'
import { Donut } from '@/shared/charts/Donut'
import { AskPanel, CouncilBoard } from '@/shared/ui/council'
import { getAssay, type Assay } from './api'
import { advise, ask, assayContext } from './analytics/council'
import { PlainStanding } from './analytics/PlainStanding'

/**
 * Dossier analytics — what the record rests on, measured.
 *
 * ## Next to the Assay
 *
 * The Assay is the instrument: a 6×6 Admiralty plate, a custody-delay profile, a chronology
 * against the floor. This is the reading. Both come from `GET /intel/assay`, so they cannot
 * disagree about a total, and neither replaces the other — the plate shows the SHAPE of the
 * record's quality, this page answers what to do about it.
 *
 * ## Every number here is a conjunction, and that is the point
 *
 * "Meets the floor" means the source is graded at or above the reliability letter AND the
 * item at or above the credibility digit. Averaging the two axes — the mistake this whole
 * feature exists to refuse — would let an A6 (an impeccable source making a claim nobody can
 * judge) pass as strong material. The floor is a staircase, never a diagonal.
 */
const WINDOWS = [7, 30, 90, 180, 365]

type Tab = 'standing' | 'sources' | 'files' | 'council'

const TABS: { id: Tab; label: string; hint: string }[] = [
  { id: 'standing', label: 'Standing',   hint: 'Can we use what we hold?' },
  { id: 'sources',  label: 'Sources',    hint: 'Who is telling us, and how good is it' },
  { id: 'files',    label: 'Files',      hint: 'Which subjects are actually covered' },
  { id: 'council',  label: 'AI Council', hint: 'The record read as decisions' },
]

export function DossierAnalyticsPage() {
  const canRead = useCan('intel:read')
  const [windowDays, setWindowDays] = useState(90)
  const [tab, setTab] = useState<Tab>('standing')

  const assay = useLoad<Assay | null>(
    () => getAssay({ windowDays, limit: 400 }), [windowDays])

  const a = assay.data
  const findings = useMemo(() => (a ? advise(a) : []), [a])

  if (!canRead) return <Forbidden what="the dossier" />

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Analytics"
        description="What this record rests on — the same payload the Assay draws, read as decisions rather than as an instrument."
        right={
          <select value={windowDays} onChange={(e) => setWindowDays(Number(e.target.value))}
                  aria-label="Window"
                  className="h-9 rounded-md border border-input bg-background px-2 text-erp-sm">
            {WINDOWS.map((d) => (
              <option key={d} value={d}>{d < 365 ? `Last ${d} days` : 'Last year'}</option>
            ))}
          </select>
        }
      />
      <ErrorStrip message={assay.error} />

      {assay.loading && !a ? <LoadingBlock label="Reading the record…" /> : !a ? null : (
        <>
          <Panel bodyClassName="p-4">
            <PlainStanding assay={a} />
          </Panel>

          <Headline assay={a} />

          <div className="flex flex-wrap gap-1 border-b border-border" role="tablist">
            {TABS.map((t) => (
              <button key={t.id} role="tab" aria-selected={tab === t.id} title={t.hint}
                      onClick={() => setTab(t.id)}
                      className={cn(
                        'px-3 py-2 text-erp-sm rounded-t-md border-b-2 -mb-px transition-colors',
                        tab === t.id
                          ? 'border-primary text-foreground font-medium'
                          : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50')}>
                {t.label}
                {t.id === 'council' && findings.some((f) => f.severity === 'critical') && (
                  <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-rose-600 text-white text-erp-xs px-1.5 min-w-[18px] h-[18px] tabular-nums">
                    {findings.filter((f) => f.severity === 'critical').length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {tab === 'standing' && <StandingTab assay={a} />}
          {tab === 'sources' && <SourcesTab assay={a} />}
          {tab === 'files' && <FilesTab assay={a} />}
          {tab === 'council' && (
            <div className="grid gap-4 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <CouncilBoard
                  findings={findings}
                  title="What this record can carry"
                  description={`${findings.length} ${findings.length === 1 ? 'finding' : 'findings'} over the last ${windowDays} days, ranked by how much of the record each puts at risk.`}
                  stakeUnit="item"
                  emptyMessage="Nothing about this record crosses a threshold. Widen the window if you are checking whether it has been degrading."
                />
              </div>
              <AskPanel
                storageKey={`dossier-council-history:${windowDays}`}
                description="Plain language. The record's figures go with the question, so the answer is about what this file can support."
                placeholder="Can we put this in a press conference?"
                scopeNote="It sees the grading, corroboration, timeliness and sources — not the item text."
                suggestions={[
                  'Can we put this in a press conference?',
                  'What would not survive a challenge?',
                  'Which sources should we drop?',
                  'What should collectors go and find?',
                  'How current is this record?',
                ]}
                onAsk={(q) => ask(q, findings)}
                context={() => assayContext(a, findings)}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

function Headline({ assay }: { assay: Assay }) {
  const s = assay.standing
  const pct = (n: number) => (s.items > 0 ? `${Math.round((n / s.items) * 100)}%` : '—')
  const hours = (m: number) => (m < 60 ? `${Math.round(m)}m`
    : m < 60 * 48 ? `${(m / 60).toFixed(0)}h` : `${Math.round(m / 1440)}d`)
  return (
    <KpiStrip>
      <MetricCard label="Items on file" value={fmtNumber(s.items)}
        sub={`${fmtNumber(s.sources)} sources · ${fmtNumber(s.subjects)} files`} />
      <MetricCard label="Strong enough to quote" value={pct(s.atOrAboveFloor)}
        sub={`meets the ${assay.floor.reliability}${assay.floor.credibility} floor on BOTH axes`}
        accent={s.items > 0 && s.atOrAboveFloor / s.items < 0.5} />
      <MetricCard label="Nobody has repeated it" value={fmtNumber(s.singleSource)}
        sub={`${fmtNumber(s.corroborated)} have a second source`} />
      <MetricCard label="Disputed or debunked" value={fmtNumber(s.disputed + s.debunked)}
        sub={s.debunked > 0 ? `${fmtNumber(s.debunked)} shown to be false` : 'none shown false'}
        accent={s.debunked > 0} />
      {/* Median AND p90 together: the median is the comfortable number and the tail is the
          one that decides whether this record can support reacting to anything. */}
      <MetricCard label="Typical delay to reach us"
        value={s.latencyP50Minutes > 0 ? hours(s.latencyP50Minutes) : '—'}
        sub={s.latencyP90Minutes > 0 ? `slowest tenth ${hours(s.latencyP90Minutes)}` : 'not enough dated items'} />
      <MetricCard label="No event date" value={fmtNumber(s.undated)}
        sub="cannot be put on a timeline" />
    </KpiStrip>
  )
}

function StandingTab({ assay }: { assay: Assay }) {
  const s = assay.standing
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Panel title="How much of the record is usable"
             description="Meeting the floor requires clearing BOTH the source grade and the credibility grade — an impeccable source making an unjudgeable claim does not qualify.">
        <OutcomeStrip parts={[
          { label: 'Meets the floor', value: s.atOrAboveFloor, tone: 'good' },
          { label: 'Below the floor', value: Math.max(s.belowFloor - s.disputed - s.debunked, 0), tone: 'warn' },
          { label: 'Disputed', value: s.disputed, tone: 'bad' },
          { label: 'Debunked', value: s.debunked, tone: 'bad' },
        ]} />
        <div className="mt-4 grid grid-cols-2 gap-3 text-erp-sm">
          <Fact label="Corroborated" value={fmtNumber(s.corroborated)} />
          <Fact label="Single source" value={fmtNumber(s.singleSource)} />
          <Fact label="Unverified" value={fmtNumber(s.unverified)} />
          <Fact label="Redacted" value={fmtNumber(s.redacted)} />
          <Fact label="Files on one source only" value={fmtNumber(s.singleSourceSubjects)} />
          <Fact label="Withheld from you" value={fmtNumber(s.withheldSensitive)} />
        </div>
      </Panel>

      <Panel title="How long things take to reach us"
             description="Time from the event to this platform taking custody. Undated is its own bucket — it is not a delay of zero.">
        <ChartFrame title="Items by delay">
          <RankedBars
            points={assay.latency.map((b) => ({ label: b.label, value: b.items }))}
            tone={(l) => (l.toLowerCase().includes('undated') ? 'warn'
              : l.toLowerCase().includes('30') || l.toLowerCase().includes('over') ? 'bad' : undefined)}
            emptyMessage="Nothing in this window."
          />
        </ChartFrame>
        <p className="mt-3 text-erp-xs text-muted-foreground">
          A record whose mass sits in the slow buckets is a history rather than an
          early-warning system. That is a legitimate thing for it to be — but it decides
          whether you can react to events or only describe them afterwards.
        </p>
      </Panel>

      <Panel title="What the record is about"
             description="Categories, by how much of each meets the floor rather than by volume alone.">
        <RankedBars
          points={assay.categories.slice(0, 12).map((c) => ({
            label: c.label,
            value: c.items,
            hint: `${c.atOrAboveFloor} of ${c.items} strong`,
          }))}
          tone={(l) => {
            const c = assay.categories.find((x) => x.label === l)
            if (!c || c.items === 0) return undefined
            return c.atOrAboveFloor / c.items < 0.3 ? 'bad'
              : c.atOrAboveFloor / c.items > 0.7 ? 'good' : 'warn'
          }}
          emptyMessage="No categories in this window."
        />
      </Panel>

      <Panel title="Corroboration"
             description="An unrepeated observation is not a discredited one — but it cannot carry an argument on its own.">
        <Donut
          slices={[
            { id: 'corr', label: 'Has a second source', value: s.corroborated },
            { id: 'single', label: 'One source only', value: s.singleSource },
            { id: 'unver', label: 'Not yet checked', value: Math.max(s.unverified - s.singleSource, 0) },
          ]}
          total={s.items}
          centreLabel="Items"
          formatValue={fmtNumber}
          emptyMessage="Nothing in this window to divide."
        />
      </Panel>
    </div>
  )
}

function SourcesTab({ assay }: { assay: Assay }) {
  const s = assay.standing
  const rows = [...assay.sources].sort((a, b) => b.items - a.items)
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Panel title="Who the record comes from"
             description="Volume by source. One source carrying most of a file means the file describes what that source chose to cover.">
        <Donut
          slices={rows.filter((r) => r.items > 0)
            .map((r) => ({ id: r.id, label: r.name, value: r.items }))}
          total={s.items}
          centreLabel="Items"
          formatValue={fmtNumber}
          emptyMessage="No source has contributed in this window."
        />
      </Panel>

      <Panel title="How much of each source's output is usable"
             description="The bar is what MEETS the floor, not what was collected — a source can be prolific and contribute nothing quotable.">
        <RankedBars
          points={rows.slice(0, 12).map((r) => ({
            label: `${r.name} (${r.reliability})`,
            value: r.atOrAboveFloor,
            hint: `${r.atOrAboveFloor} of ${r.items} meet the floor`
              + (r.contested > 0 ? ` · ${r.contested} contested` : ''),
          }))}
          tone={(l) => {
            const r = rows.find((x) => `${x.name} (${x.reliability})` === l)
            if (!r || r.items === 0) return undefined
            return r.atOrAboveFloor / r.items < 0.3 ? 'bad'
              : r.atOrAboveFloor / r.items > 0.7 ? 'good' : 'warn'
          }}
          emptyMessage="No source has contributed in this window."
        />
        <p className="mt-3 text-erp-xs text-muted-foreground">
          A source whose bar is far shorter than its share of the donut beside it is adding
          volume without adding evidence. Re-grade it or stop collecting from it.
        </p>
      </Panel>
    </div>
  )
}

function FilesTab({ assay }: { assay: Assay }) {
  const rows = [...assay.subjects].sort((a, b) => b.items - a.items)
  const thin = rows.filter((r) => r.items > 0 && r.atOrAboveFloor === 0)
  return (
    <div className="space-y-4">
      {thin.length > 0 && (
        <Panel title="Files that look populated and are not"
               description="Every item in these is below the floor. They cannot be quoted from at all.">
          <ul className="flex flex-wrap gap-2">
            {thin.map((r) => (
              <li key={r.id}
                  className="rounded-full border border-amber-500/60 bg-amber-500/10 px-2.5 py-1 text-erp-xs">
                {r.displayName}
                <span className="ml-1.5 tabular-nums text-muted-foreground">
                  {fmtNumber(r.items)} items · 0 usable
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <Panel title="Coverage by file"
             description="Usable items per subject. The count beside each is how many sources it rests on — one is a single point of failure.">
        <RankedBars
          points={rows.slice(0, 15).map((r) => ({
            label: r.displayName,
            value: r.atOrAboveFloor,
            hint: `${r.atOrAboveFloor} of ${r.items} usable · ${r.sources} `
              + `${r.sources === 1 ? 'source' : 'sources'}`
              + (r.collectionEnabled ? '' : ' · collection off'),
          }))}
          tone={(l) => {
            const r = rows.find((x) => x.displayName === l)
            if (!r) return undefined
            return r.atOrAboveFloor === 0 ? 'bad' : r.sources === 1 ? 'warn' : 'good'
          }}
          emptyMessage="No file has an item in this window."
        />
      </Panel>
    </div>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2 border-b border-border/60 pb-1">
      <span className="text-erp-xs text-muted-foreground">{label}</span>
      <span className="tabular-nums font-medium">{value}</span>
    </div>
  )
}
